/**
 * TradeDataList — configurable data-list renderer for the trade portal.
 *
 * Renders grid-based lists (quotes, orders) from a declarative config object.
 * Columns, rows, features, and rendering callbacks are all config-driven.
 * Includes a factory for standard quote/order list configs and a helper to
 * sync search/sort control visibility with feature flags.
 *
 * Usage:
 *   const config = window.createTradeRecordListConfig('quote', { rows: quotes });
 *   const html = window.renderTradeDataList(config);
 *   document.getElementById('list').innerHTML = html;
 */
(function () {
	/* ── helpers ── */

	const escapeTradeHtml =
		window.escapeTradeHtml ||
		function (value) {
			if (value === null || value === undefined) return '';
			return String(value)
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;');
		};

	function normalizeTradeClassName(input) {
		if (!input) return '';
		if (Array.isArray(input)) {
			return input.filter(Boolean).join(' ').trim();
		}
		return String(input).trim();
	}

	function buildTradeAttributes(attributes) {
		if (!attributes) return '';
		return Object.keys(attributes)
			.filter(function (key) {
				return (
					attributes[key] !== null &&
					attributes[key] !== undefined &&
					attributes[key] !== false
				);
			})
			.map(function (key) {
				const value = attributes[key];
				if (value === true) return key;
				return key + '="' + escapeTradeHtml(value) + '"';
			})
			.join(' ');
	}

	/* ── rendering ── */

	function getTradeDataListTemplate(columns) {
		return (Array.isArray(columns) ? columns : [])
			.map(function (column) {
				return column && column.width ? column.width : 'minmax(0, 1fr)';
			})
			.join(' ');
	}

	function renderTradeDataListHeader(columns, template) {
		const headerCells = (Array.isArray(columns) ? columns : [])
			.map(function (column) {
				const label = column && column.label ? escapeTradeHtml(column.label) : '';
				const classes = ['trade-data-list__header-cell'];
				const columnHeaderClass = normalizeTradeClassName(column && column.headerClassName);
				if (columnHeaderClass) classes.push(columnHeaderClass);
				if (!label) classes.push('trade-data-list__header-cell--empty');

				return (
					'<div class="' +
					classes.join(' ') +
					'">' +
					(label
						? '<span class="trade-data-list__header-text">' + label + '</span>'
						: '&nbsp;') +
					'</div>'
				);
			})
			.join('');

		return (
			'<div class="trade-data-list__header">' +
			'<div class="trade-data-list__header-grid" style="--trade-data-list-columns:' +
			template +
			';">' +
			headerCells +
			'</div>' +
			'</div>'
		);
	}

	function renderTradeDataListCell(column, row, context) {
		const settings = column || {};
		const content =
			typeof settings.renderCell === 'function'
				? settings.renderCell(row, context)
				: escapeTradeHtml(row && settings.key ? row[settings.key] : '');
		const classes = ['trade-data-list__cell'];
		const cellClassName =
			typeof settings.cellClassName === 'function'
				? settings.cellClassName(row, context)
				: settings.cellClassName;
		const normalizedCellClassName = normalizeTradeClassName(cellClassName);
		if (normalizedCellClassName) classes.push(normalizedCellClassName);
		if (settings.hideOnMobile) classes.push('trade-data-list__cell--hide-mobile');
		if (settings.unstyled) classes.push('trade-data-list__cell--unstyled');

		const label = settings.mobileLabel || settings.label || '';
		const showMobileLabel =
			context &&
			context.features &&
			context.features.stackedMobile &&
			!settings.hideMobileLabel &&
			label;
		const labelHTML = showMobileLabel
			? '<span class="trade-data-list__cell-label">' + escapeTradeHtml(label) + '</span>'
			: '';

		if (settings.unstyled) {
			return '<div class="' + classes.join(' ') + '">' + content + '</div>';
		}

		return (
			'<div class="' +
			classes.join(' ') +
			'">' +
			'<div class="trade-data-list__cell-content">' +
			labelHTML +
			content +
			'</div>' +
			'</div>'
		);
	}

	function renderTradeDataListRow(config, row, rowIndex) {
		const settings = config || {};
		const columns = Array.isArray(settings.columns) ? settings.columns : [];
		const features = settings.features || {};
		const template = getTradeDataListTemplate(columns);
		const rowClasses = ['trade-list-item', 'trade-data-list__row'];
		const rowClassName =
			typeof settings.rowClassName === 'function'
				? settings.rowClassName(row, rowIndex)
				: settings.rowClassName;
		const normalizedRowClassName = normalizeTradeClassName(rowClassName);
		if (normalizedRowClassName) rowClasses.push(normalizedRowClassName);
		if (
			features.clickableRows &&
			(typeof settings.getRowClickable !== 'function' ||
				settings.getRowClickable(row, rowIndex) !== false)
		) {
			rowClasses.push('trade-data-list__row--clickable');
		}

		const attributes = Object.assign(
			{},
			typeof settings.rowAttributes === 'function'
				? settings.rowAttributes(row, rowIndex)
				: settings.rowAttributes,
		);
		const attributeString = buildTradeAttributes(attributes);
		const cellsHTML = columns
			.map(function (column) {
				return renderTradeDataListCell(column, row, {
					rowIndex: rowIndex,
					features: features,
				});
			})
			.join('');

		return (
			'<div class="' +
			rowClasses.join(' ') +
			'"' +
			(attributeString ? ' ' + attributeString : '') +
			'>' +
			'<div class="trade-data-list__row-grid" style="--trade-data-list-columns:' +
			template +
			';">' +
			cellsHTML +
			'</div>' +
			'</div>'
		);
	}

	/* ── exported API ── */

	/**
	 * Renders a complete data list (header + body rows) as an HTML string.
	 * @param {Object} config - List configuration (columns, rows, features, variant, rowClassName, rowAttributes)
	 * @returns {string} Complete data-list HTML
	 */
	function renderTradeDataList(config) {
		const settings = config || {};
		const rows = Array.isArray(settings.rows) ? settings.rows : [];
		const columns = Array.isArray(settings.columns) ? settings.columns : [];
		const features = settings.features || {};
		const listClasses = ['trade-data-list'];
		const variant = settings.variant ? 'trade-data-list--' + settings.variant : '';
		if (variant) listClasses.push(variant);
		if (features.showHeaders) listClasses.push('trade-data-list--with-headers');
		if (features.stackedMobile) listClasses.push('trade-data-list--stacked-mobile');

		let html = '<div class="' + listClasses.join(' ') + '">';
		if (features.showHeaders) {
			html += renderTradeDataListHeader(columns, getTradeDataListTemplate(columns));
		}
		html +=
			'<div class="trade-data-list__body">' +
			rows
				.map(function (row, rowIndex) {
					return renderTradeDataListRow(settings, row, rowIndex);
				})
				.join('') +
			'</div></div>';
		return html;
	}

	function renderTradeAvatarStack(users) {
		const items = Array.isArray(users) ? users : [];
		return items
			.map(function (user) {
				return (
					'<span class="avatar">' +
					escapeTradeHtml(user && user.initials ? user.initials : '') +
					'</span>'
				);
			})
			.join('');
	}

	/* ── record list config ── */

	/**
	 * Creates a standard list config for quote or order records.
	 * @param {'quote'|'order'} type - Record type
	 * @param {Object} options - Override options (rows, features)
	 * @returns {Object} Config object suitable for renderTradeDataList
	 */
	function createTradeRecordListConfig(type, options) {
		const settings = options || {};
		const rows = Array.isArray(settings.rows) ? settings.rows : [];
		const isQuote = type === 'quote';
		const isOrder = type === 'order';
		const featureDefaults = {
			showHeaders: false,
			clickableRows: false,
			showStatusBadge: true,
			showAssignedUsers: true,
			showOpenLink: true,
			showMenuIndicator: true,
			showSearch: true,
			showSort: true,
		};
		const features = Object.assign({}, featureDefaults, settings.features || {});

		function getAssignedUsers(item) {
			const assignedUsers = Array.isArray(item && item.assignedUsers)
				? item.assignedUsers
				: [];
			return isQuote ? assignedUsers.slice(0, 3) : assignedUsers;
		}

		function getStatusDisplay(item) {
			if (isQuote && item && item.status === 'Converted' && item.linkedOrderId) {
				return item.linkedOrderDisplayId
					? 'ORDER #' + escapeTradeHtml(item.linkedOrderDisplayId)
					: 'ORDER';
			}
			if (isOrder && item && item.isProcessing) {
				return 'Processing';
			}
			return escapeTradeHtml(item && item.status ? item.status : '');
		}

		function getRecordDisplayId(item) {
			if (!item) return '';
			return item.displayId;
		}

		function getOpenLink(item) {
			if (isQuote && item && item.status === 'Converted' && item.linkedOrderId) {
				return {
					label: 'OPEN ORDER',
					href: '/pages/orders?id=' + item.linkedOrderId,
				};
			}
			if (isQuote && item && item.status === 'Processing') {
				return null;
			}
			if (isQuote) {
				return {
					label: 'OPEN QUOTE',
					href: '/pages/quotes?id=' + item.id,
				};
			}
			if (isOrder && item && item.isProcessing) {
				return null;
			}
			return {
				label: 'OPEN ORDER',
				href: '/pages/orders?id=' + item.id,
			};
		}

		function renderSummaryCell(item) {
			const amount = window.formatTradeAmount(item && item.summary ? item.summary.total : 0);
			const typeLabel = isQuote ? 'QUOTE' : 'ORDER';
			if (isOrder && item && item.isProcessing) {
				return (
					'<div class="trade-list-item__group">' +
					'<div class="trade-list-item__info">' +
					'<span class="trade-list-item__label">' +
					typeLabel +
					' NO. #' +
					escapeTradeHtml(getRecordDisplayId(item)) +
					'</span>' +
					'<span class="trade-list-item__value">' +
					escapeTradeHtml(item.title) +
					'</span>' +
					'</div>' +
					'<div class="trade-list-item__info">' +
					'<span class="trade-list-item__label">' +
					typeLabel +
					' AMOUNT</span>' +
					'<span class="trade-list-item__value">' +
					amount +
					'</span>' +
					'</div>' +
					'</div>'
				);
			}

			const middleLabel = isQuote ? 'SHIPPING ADDRESS' : 'SHIPPING STATUS';
			const middleValue = isQuote
				? escapeTradeHtml(item && item.shippingAddress ? item.shippingAddress.city : '')
				: escapeTradeHtml(item && item.shippingStatus ? item.shippingStatus : '');

			return (
				'<div class="trade-list-item__group">' +
				'<div class="trade-list-item__info">' +
				'<span class="trade-list-item__label">' +
				typeLabel +
				' NO. #' +
				escapeTradeHtml(getRecordDisplayId(item)) +
				'</span>' +
				'<span class="trade-list-item__value">' +
				escapeTradeHtml(item && item.title) +
				'</span>' +
				'</div>' +
				'<div class="trade-list-item__info quote-info__hide-mobile">' +
				'<span class="trade-list-item__label">' +
				middleLabel +
				'</span>' +
				'<span class="trade-list-item__value">' +
				middleValue +
				'</span>' +
				'</div>' +
				'<div class="trade-list-item__info">' +
				'<span class="trade-list-item__label">' +
				typeLabel +
				' AMOUNT</span>' +
				'<span class="trade-list-item__value">' +
				amount +
				'</span>' +
				'</div>' +
				'</div>'
			);
		}

		function renderActionsCell(item) {
			const segments = [];
			if (features.showStatusBadge) {
				segments.push('<div class="status">' + getStatusDisplay(item) + '</div>');
			}
			if (features.showAssignedUsers) {
				segments.push(
					'<div class="user-avatars">' +
						renderTradeAvatarStack(getAssignedUsers(item)) +
						'</div>',
				);
			}
			if (features.showOpenLink) {
				const link = getOpenLink(item);
				if (link) {
					segments.push(
						'<a href="' +
							escapeTradeHtml(link.href) +
							'" class="open-quote-link">' +
							escapeTradeHtml(link.label) +
							'</a>',
					);
				} else {
					/* Reserve CTA column width when the open action is unavailable (e.g. processing). */
					const placeholderLabel = isQuote ? 'OPEN QUOTE' : 'OPEN ORDER';
					segments.push(
						'<span class="open-quote-link open-quote-link--placeholder" aria-hidden="true">' +
							escapeTradeHtml(placeholderLabel) +
							'</span>',
					);
				}
			}
			if (features.showMenuIndicator && !(isOrder && item && item.isProcessing) && !(isQuote && item && item.status === 'Processing')) {
				segments.push(
					'<div class="quote-actions__menu-icon">' +
						(window.TRADE_ARROW_ICON_SVG || '') +
						'</div>',
				);
			}

			return '<div class="quote-actions">' + segments.join('') + '</div>';
		}

		return {
			variant: isQuote ? 'quotes' : 'orders',
			rows: rows,
			features: features,
			columns: [
				{
					key: 'summary',
					width: 'minmax(0, 1fr)',
					unstyled: true,
					renderCell: renderSummaryCell,
				},
				{
					key: 'actions',
					width: 'auto',
					unstyled: true,
					renderCell: renderActionsCell,
				},
			],
			rowClassName: function (item) {
				if (isOrder && item && item.isProcessing) return 'trade-list-item--processing';
				if (isQuote && item && item.status === 'Processing') return 'trade-list-item--processing';
				return '';
			},
			rowAttributes: function (item) {
				const attributes = {};
				if (isQuote) {
					attributes['data-quote-id'] = item && item.id;
					if (item && item.linkedOrderId) {
						attributes['data-linked-order-id'] = item.linkedOrderId;
					}
				} else {
					if (!(item && item.isProcessing)) {
						attributes['data-order-id'] = item && item.id;
					}
				}
				return attributes;
			},
			getRowClickable: function (item) {
				if (isOrder && item && item.isProcessing) return false;
				if (isQuote && item && item.status === 'Processing') return false;
				return true;
			},
		};
	}

	/* ── list controls ── */

	/**
	 * Shows or hides search/sort controls based on feature flags in the config.
	 * @param {Object} config - Contains features, searchContainerEl, sortContainerEl, headerControlsEl
	 */
	function syncTradeListControls(config) {
		const settings = config || {};
		const features = settings.features || {};
		const searchContainerEl = settings.searchContainerEl || null;
		const sortContainerEl = settings.sortContainerEl || null;
		const headerControlsEl = settings.headerControlsEl || null;
		const showSearch = features.showSearch !== false;
		const showSort = features.showSort !== false;

		if (searchContainerEl) {
			searchContainerEl.style.display = showSearch ? '' : 'none';
		}

		if (sortContainerEl) {
			sortContainerEl.style.display = showSort ? '' : 'none';
		}

		if (headerControlsEl) {
			headerControlsEl.style.display = showSearch || showSort ? '' : 'none';
		}
	}

	/* ── exports ── */

	window.renderTradeDataList = renderTradeDataList;
	window.renderTradeDataListRow = renderTradeDataListRow;
	window.createTradeRecordListConfig = createTradeRecordListConfig;
	window.syncTradeListControls = syncTradeListControls;
})();
